import React, { useState, useMemo } from "react";
import { Table, DatePicker, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import { CheckSquare } from "lucide-react";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetSalesReportCheques } from "../../../Api";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

const { RangePicker } = DatePicker;

export const ChequesReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);

  const startDateStr = dateRange[0].format("YYYY-MM-DD");
  const endDateStr = dateRange[1].format("YYYY-MM-DD");

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["cheque-report", startDateStr, endDateStr],
    queryFn: () =>
      GetSalesReportCheques({
        start_date: startDateStr,
        end_date: endDateStr,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const received = reportData
      .filter((x: any) => x.type === "inward")
      .reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
    const issued = reportData
      .filter((x: any) => x.type === "outward")
      .reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
    const cleared = reportData.filter(
      (x: any) => x.status_code === "cleared",
    ).length;
    const pending = reportData.filter(
      (x: any) => x.status_code === "pending",
    ).length;
    return [
      {
        title: "Total Inward Received Cheques",
        value: formatLKR(received),
        colorClass: "text-purple-600",
      },
      {
        title: "Total Outward Issued Cheques",
        value: formatLKR(issued),
        colorClass: "text-indigo-655",
      },
      {
        title: "Cleared / Pending Counts",
        value: `${cleared} Cleared / ${pending} Pending`,
        colorClass: "text-slate-655",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Cheque Date",
      dataIndex: "cheque_date",
      key: "cheque_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500 font-medium">
          {dayjs(v).format("YYYY-MM-DD")}
        </span>
      ),
    },
    {
      title: "Cheque No",
      dataIndex: "cheque_no",
      key: "cheque_no",
      render: (v: string) => (
        <span className="text-xs font-extrabold text-gray-800">{v}</span>
      ),
    },
    {
      title: "Direction",
      dataIndex: "type",
      key: "type",
      render: (v: string) => {
        const color = v === "inward" ? "purple" : "blue";
        return (
          <Tag
            color={color}
            className="rounded-full font-bold text-[10px] uppercase"
          >
            {v === "inward" ? "Received (Inward)" : "Issued (Outward)"}
          </Tag>
        );
      },
    },
    {
      title: "Issuer Bank",
      dataIndex: "bank_name",
      key: "bank_name",
      render: (v: string) => (
        <span className="text-xs text-gray-655 font-medium">{v}</span>
      ),
    },
    {
      title: "Deposit Bank Account",
      dataIndex: "bank_account_name",
      key: "bank_account_name",
      render: (v: string) => (
        <span className="text-xs text-gray-500 font-semibold">
          {v || "Not Deposited Yet"}
        </span>
      ),
    },
    {
      title: "Clearance Status",
      dataIndex: "status_name",
      key: "status_name",
      render: (v: string, record: any) => {
        let color = "default";
        if (record.status_code === "cleared") color = "success";
        if (record.status_code === "pending") color = "warning";
        if (record.status_code === "bounced") color = "error";
        return (
          <Tag
            color={color}
            className="rounded-full font-bold text-[10px] uppercase"
          >
            {v}
          </Tag>
        );
      },
    },
    {
      title: "Amount Value",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-black text-gray-800">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Reporting Date Period
        </span>
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            dates && dates[0] && dates[1] && setDateRange([dates[0], dates[1]])
          }
          allowClear={false}
          className="h-9 rounded-lg border border-gray-200 shadow-sm text-xs font-semibold"
        />
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Cheque Ledger Report"
      description="Monitor received or issued cheques, bank clearance accounts, and statuses."
      icon={<CheckSquare className="w-6 h-6 text-purple-600" />}
      backPath="/system/sales-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) =>
            record.id || record.cheque_no || Math.random().toString()
          }
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
